import React, { useMemo, useCallback } from "react";
import {
  Box,
  Title,
  Text,
  Paper,
  Stack,
  Group,
  ActionIcon,
} from "@mantine/core";
import { useStyles } from "../../styles/styles";
import type { Section, SelectedDoc } from "../../types";
import { chunkItems, flattenContent } from "../../utils";
import LabelItem from "./items/LabelItem";
import FieldItem from "./items/FieldItem";
import DocumentItem from "./items/DocumentItem";
import TaxInfoDocPreview from "./TaxInfoDocPreview";
import { useProfileStore } from "../../store/profileStore";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

interface TaxInfoPreviewProps {
  sections: Section[];
  sectionsRefs: React.RefObject<HTMLDivElement>[];
}

const TaxInfoPreview: React.FC<TaxInfoPreviewProps> = ({
  sections,
  sectionsRefs,
}) => {
  const { classes } = useStyles();

  // Simple helper function - no memoization needed for trivial operations
  const isTextBasedSection = (sectionKey: string): boolean => {
    return sectionKey === "comments";
  };

  const { headerData, activeYear, contentData, documents } = useProfileStore();

  // Memoize document items - documents is now a flat array after refactoring
  const allDocumentItems = useMemo(() => documents || [], [documents]);

  const selectedDoc = useProfileStore((state) => state.selectedDoc);
  const setSelectedDoc = useProfileStore((state) => state.setSelectedDoc);
  const setActiveDocument = useProfileStore((state) => state.setActiveDocument);

  // Memoize current index calculation - expensive array search
  const currentIndex = useMemo(
    () =>
      allDocumentItems.findIndex((item) => item.docId === selectedDoc?.docId),
    [allDocumentItems, selectedDoc?.docId]
  );

  const selectDoc = (doc: SelectedDoc): void => {
    setSelectedDoc(doc);
    setActiveDocument(doc.docId ?? null);
  };

  const handlePrev = useCallback((): void => {
    if (currentIndex > 0) {
      const prevDoc = allDocumentItems[currentIndex - 1];
      selectDoc(prevDoc);
    }
  }, [currentIndex, allDocumentItems, selectDoc]);

  const handleNext = useCallback((): void => {
    if (currentIndex < allDocumentItems.length - 1) {
      const nextDoc = allDocumentItems[currentIndex + 1];
      selectDoc(nextDoc);
    }
  }, [currentIndex, allDocumentItems, selectDoc]);

  // Construct full name with proper fallback logic
  const firstName = headerData?.basicInfo?.firstName;
  const lastName = headerData?.basicInfo?.lastName;
  const fullName =
    firstName || lastName
      ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
      : "Client";

  // Pre-compute all sections data with aggressive memoization
  const sectionsData = useMemo(() => {
    if (!contentData) return [];

    return sections
      .map((section) => {
        const sectionData = contentData[section.key] ?? {};

        // Early return for empty sections
        if (sectionData.items?.length === 0) return null;

        const allItems = sectionData ? flattenContent(sectionData) : [];

        // Early return for empty items
        if (allItems.length === 0) return null;

        const pages = chunkItems(allItems);

        return {
          section,
          allItems,
          pages,
          isTextBased: isTextBasedSection(section.key),
        };
      })
      .filter(Boolean);
  }, [contentData, sections, isTextBasedSection]);

  // Render sections directly - memoization of JSX with refs and styles is problematic
  const renderedSections = sectionsData.map((sectionData, index) => {
    if (!sectionData) return null;

    const { section, pages, isTextBased } = sectionData;
    const sectionRef = sectionsRefs[index];

    if (isTextBased) {
      return (
        <div ref={sectionRef} key={section.key}>
          <Paper withBorder className={classes.emptyPaper}>
            <Title order={2} style={{ marginBottom: "0.5rem" }}>
              {section.label}
            </Title>
            <Text size="sm" className={classes.italicText}>
              No data available.
            </Text>
          </Paper>
        </div>
      );
    }

    let isVeryFirstPage = true;

    return (
      <div ref={sectionRef} key={section.key}>
        {pages.map((pageItems, pageIndex) => {
          const isFirstPage = pageIndex === 0;
          const displayLabel = isFirstPage
            ? section.label
            : `${section.label} (Continued)`;

          const showWelcome = isVeryFirstPage;
          if (isVeryFirstPage) isVeryFirstPage = false;

          return (
            <React.Fragment key={`${section.key}-page-${pageIndex}`}>
              <Text
                c="dimmed"
                size="sm"
                ml={{ base: "5px", sm: "12px" }}
                className={
                  isFirstPage ? classes.sectionLabel : classes.continuedLabel
                }
              >
                {displayLabel}
              </Text>
              <Paper withBorder className={classes.paper}>
                {showWelcome && (
                  <Stack className={classes.fieldHeader}>
                    <Text fw={500}>{activeYear} Tax Organizer (1040)</Text>
                    <Text fw={500}>{fullName}</Text>
                  </Stack>
                )}
                <div className={classes.grid}>
                  {pageItems.map((item) => {
                    const gridColumnSpan =
                      item.type === "label" ? "1 / -1" : "span 1";

                    return (
                      <div
                        key={item.key}
                        style={{
                          gridColumn: gridColumnSpan,
                          width: "100%",
                        }}
                      >
                        {item.type === "label" && <LabelItem item={item} />}
                        {item.type === "field" && <FieldItem item={item} />}
                        {item.type === "document" && (
                          <DocumentItem
                            item={{
                              ...item,
                              url: item.presignedUrl ?? "",
                              fileType: "unknown",
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Paper>
            </React.Fragment>
          );
        })}
      </div>
    );
  });

  return (
    <Box className={classes.container}>
      {selectedDoc?.presignedUrl ? (
        <>
          {/* View next buttons */}
          <Group justify="space-between" ml={"30px"} mr={"30px"}>
            <ActionIcon
              variant="transparent"
              onClick={handlePrev}
              disabled={currentIndex <= 0}
            >
              <IconChevronLeft size={30} />
            </ActionIcon>{" "}
            <Text size="md" c={"dimmed"}>
              {selectedDoc?.label}
            </Text>
            <ActionIcon
              variant="transparent"
              onClick={handleNext}
              disabled={currentIndex >= allDocumentItems.length - 1}
            >
              <IconChevronRight size={30} />
            </ActionIcon>
          </Group>

          <TaxInfoDocPreview selectedDoc={selectedDoc} />
        </>
      ) : (
        <div style={{ position: "relative" }}>{renderedSections}</div>
      )}
    </Box>
  );
};

export default TaxInfoPreview;
